import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { 
  DocumentTextIcon, 
  PencilIcon, 
  PrinterIcon,
  CheckCircleIcon,
  ClockIcon,
  ClockIcon as HistoryIcon,
  ArrowLeftIcon,
  DocumentArrowDownIcon,
  PaperAirplaneIcon,
  UserGroupIcon
} from "@heroicons/react/24/outline";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import RoleOnly, { useUserHasAccess } from "../../../../routes/roleOnly";
import { getHseDocument, getDocumentVersions, approveDocumentVersion, submitDocumentForApproval, printDocument, getVersionApprovals, acknowledgeDocumentVersionRead } from "../../../../api/hse/documents";
import { DocumentAccessDialog } from "../../../../components/dashboard/documents/DocumentAccessDialog";
import { DocumentFileContent } from "./DocumentFileContent";
import "../../../../components/utils/PromangRichTextEditor.css";

/**
 * HSE/KS Document View - View document details with version history and print
 */
export const HseKsDocumentView = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.session.currentUser);
  const [document, setDocument] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedVersionId, setSelectedVersionId] = useState(null); // Selected version to display
  const [displayContent, setDisplayContent] = useState(null); // Content of selected version
  const [displayAttachedDocument, setDisplayAttachedDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showAccessDialog, setShowAccessDialog] = useState(false);
  const [approvingVersion, setApprovingVersion] = useState(null);
  const [printingVersionId, setPrintingVersionId] = useState(null);
  const [myAcknowledged, setMyAcknowledged] = useState(null);
  const [ackSubmitting, setAckSubmitting] = useState(false);
  
  // All hooks must be called before any conditional logic
  const canEdit = useUserHasAccess({ role: "hse_ks_documents", action: "update" });
  const canApproveRole = useUserHasAccess({ role: "hse_ks_documents_approve", action: "approve" });
  
  // Conditional logic after hooks
  const isAdmin = currentUser?.roles?.some((r) => r.name === "admin");
  const canApprove = isAdmin || canApproveRole;

  useEffect(() => {
    fetchDocument();
    fetchVersions();
  }, [id]);

  // Refresh versions when document changes (e.g., after creating revision)
  useEffect(() => {
    if (document) {
      fetchVersions();
    }
  }, [document?.status, document?.current_version]);

  const resolveVersionAttachment = (version) =>
    version?.attached_document ||
    (version?.attached_document_id ? { id: version.attached_document_id } : null);

  // Determine which version to display by default
  useEffect(() => {
    if (versions.length > 0 && !selectedVersionId) {
      // Find approved version first (preferred default)
      const approvedVersion = versions.find(v => v.status === 'approved' && v.is_approved);
      if (approvedVersion) {
        setSelectedVersionId(approvedVersion.id);
        setDisplayContent(approvedVersion.content);
        setDisplayAttachedDocument(resolveVersionAttachment(approvedVersion));
        return;
      }
      
      // If no approved version, use the latest version
      const latestVersion = versions[0];
      if (latestVersion) {
        setSelectedVersionId(latestVersion.id);
        setDisplayContent(latestVersion.content);
        setDisplayAttachedDocument(resolveVersionAttachment(latestVersion));
      }
    }
  }, [versions, selectedVersionId]);

  // Update display content when selected version changes
  useEffect(() => {
    if (selectedVersionId && versions.length > 0) {
      const selectedVersion = versions.find(v => v.id === selectedVersionId);
      if (selectedVersion) {
        setDisplayContent(selectedVersion.content);
        setDisplayAttachedDocument(resolveVersionAttachment(selectedVersion));
      }
    }
  }, [selectedVersionId, versions]);

  useEffect(() => {
    let cancelled = false;
    const loadAck = async () => {
      if (!id || !selectedVersionId || !currentUser?.id) {
        setMyAcknowledged(null);
        return;
      }
      const v = versions.find((x) => x.id === selectedVersionId);
      if (!v || v.status !== "approved") {
        setMyAcknowledged(null);
        return;
      }
      try {
        const res = await getVersionApprovals(id, selectedVersionId);
        if (cancelled) return;
        const mine = res.approvals?.find((a) => a.user_id === currentUser.id);
        // read_understood_at = explicit read receipt; approved === true may be legacy read-ack or workflow vote on published versions
        setMyAcknowledged(!!mine?.read_understood_at || mine?.approved === true);
      } catch {
        if (!cancelled) setMyAcknowledged(null);
      }
    };
    loadAck();
    return () => {
      cancelled = true;
    };
  }, [id, selectedVersionId, versions, currentUser?.id]);

  // Scroll to latest version after versions are loaded (if coming from edit page)
  useEffect(() => {
    if (versions.length > 0 && document?.status === 'draft') {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        // Use window.document to avoid conflict with React state variable named 'document'
        const latestVersionElement = window.document.querySelector(`[data-version-id="${versions[0].id}"]`);
        if (latestVersionElement) {
          latestVersionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Highlight the version briefly
          latestVersionElement.classList.add('bg-blue-50');
          setTimeout(() => {
            latestVersionElement.classList.remove('bg-blue-50');
          }, 2000);
        }
      }, 100);
    }
  }, [versions, document?.status]);

  const fetchDocument = async () => {
    try {
      setLoading(true);
      setError(false);
      const response = await getHseDocument(id);
      setDocument(response.document);
    } catch (err) {
      console.error("Error fetching document:", err);
      setError(true);
      toast.error(t("Kunne ikke laste dokument"));
    } finally {
      setLoading(false);
    }
  };

  const fetchVersions = async () => {
    try {
      const response = await getDocumentVersions(id);
      const versionsList = response.versions || [];
      setVersions(versionsList);
      
      // If no version is selected yet, select approved version or latest
      if (!selectedVersionId && versionsList.length > 0) {
        const approvedVersion = versionsList.find(v => v.status === 'approved' && v.is_approved);
        if (approvedVersion) {
          setSelectedVersionId(approvedVersion.id);
          setDisplayContent(approvedVersion.content);
          setDisplayAttachedDocument(resolveVersionAttachment(approvedVersion));
        } else if (versionsList.length > 0) {
          // Use latest version if no approved version
          setSelectedVersionId(versionsList[0].id);
          setDisplayContent(versionsList[0].content);
          setDisplayAttachedDocument(resolveVersionAttachment(versionsList[0]));
        }
      }
    } catch (err) {
      console.error("Error fetching versions:", err);
      // Don't show error toast, just log it
    }
  };

  const handlePrint = async (versionId = null) => {
    // Prevent multiple clicks
    if (printingVersionId !== null) {
      return;
    }
    
    try {
      setPrintingVersionId(versionId);
      const response = await printDocument(id, versionId, 'document');
      if (response.pdf_url) {
        window.open(response.pdf_url, '_blank');
        toast.success(t("Dokument skrives ut..."));
      } else {
        toast.error(t("Kunne ikke generere PDF"));
      }
    } catch (err) {
      console.error("Error printing document:", err);
      const errorMessage = err.response?.data?.errors?.[0] || err.message || t("Kunne ikke skrive ut dokument");
      toast.error(errorMessage);
    } finally {
      setPrintingVersionId(null);
    }
  };

  const handleCreateRevision = async () => {
    try {
      // Check if there's a draft version - if so, show it first
      const draftVersion = versions.find(v => v.status === 'draft');
      if (draftVersion) {
        setSelectedVersionId(draftVersion.id);
        setDisplayContent(draftVersion.content);
        setDisplayAttachedDocument(resolveVersionAttachment(draftVersion));
        // Scroll to version in history
        setTimeout(() => {
          const versionElement = window.document.querySelector(`[data-version-id="${draftVersion.id}"]`);
          if (versionElement) {
            versionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            versionElement.classList.add('bg-blue-50');
            setTimeout(() => {
              versionElement.classList.remove('bg-blue-50');
            }, 2000);
          }
        }, 100);
      } else {
        // Navigate to edit page with revision flag to create new revision
        navigate(`/dashboard/hse-ks/documents/${id}/edit?revision=true`);
      }
    } catch (err) {
      toast.error(t("Kunne ikke opprette revisjon"));
    }
  };

  const handleSubmitForApproval = async () => {
    try {
      await submitDocumentForApproval(id);
      toast.success(t("Dokument sendt til godkjenning"));
      fetchDocument();
      fetchVersions();
    } catch (err) {
      console.error("Error submitting for approval:", err);
      const errorMessage = err.response?.data?.errors?.[0] || t("Kunne ikke sende dokument til godkjenning");
      toast.error(errorMessage);
    }
  };

  const handleAcknowledgeRead = async () => {
    if (!selectedVersionId || ackSubmitting) return;
    try {
      setAckSubmitting(true);
      await acknowledgeDocumentVersionRead(id, selectedVersionId, null);
      toast.success(t("hse.document.acknowledge_read_success"));
      setMyAcknowledged(true);
      await fetchDocument();
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.errors?.[0] || t("hse.document.acknowledge_read_error");
      toast.error(msg);
    } finally {
      setAckSubmitting(false);
    }
  };

  const handleApproveVersion = async (versionId) => {
    try {
      setApprovingVersion(versionId);
      await approveDocumentVersion(id, versionId);
      toast.success(t("Versjon godkjent"));
      fetchDocument();
      fetchVersions();
    } catch (err) {
      console.error("Error approving version:", err);
      toast.error(t("Kunne ikke godkjenne versjon"));
    } finally {
      setApprovingVersion(null);
    }
  };


  if (loading) {
    return <ResourceLoading size="base" />;
  }

  if (error || !document) {
    return <ResourceError errors={[t("Kunne ikke laste dokument")]} />;
  }

  // Get selected version to check its status
  const selectedVersion = versions.find(v => v.id === selectedVersionId);
  const selectedVersionStatus = selectedVersion?.status || document?.status;
  const isSelectedVersionApproved = selectedVersion?.is_approved || selectedVersion?.status === 'approved';
  
  // Check if document can be approved (pending approval and user has permission)
  const canApproveDocument = canApprove && document.status === 'pending_approval';
  // Check if document can be submitted for approval
  // Don't show if selected version is already approved
  // Only show if selected version is draft or if no version is selected and document status allows it
  const canSubmitForApproval = canEdit && 
    !isSelectedVersionApproved &&
    document.status !== 'pending_approval' && 
    (selectedVersionStatus === 'draft' || 
     (!selectedVersionId && (document.status === 'draft' || document.status === 'approved' || !document.status || document.status === null)));

  // Check if current selected version can be printed
  const canPrintCurrentVersion = selectedVersionId 
    ? versions.find(v => v.id === selectedVersionId)?.status === 'approved' || isAdmin
    : document?.has_approved_version || isAdmin;
  
  const headerActions = [
    {
      component: null,
      label: printingVersionId !== null ? t("Genererer PDF...") : t("Skriv ut"),
      action: () => handlePrint(selectedVersionId),
      icon: PrinterIcon,
      textColor: "text-gray-700",
      bgColor: "bg-white",
      hidden: false,
      disabled: !canPrintCurrentVersion || printingVersionId !== null,
      loading: printingVersionId !== null,
      menu: true,
    },
    ...(canSubmitForApproval ? [{
      component: null,
      label: t("Send til godkjenning"),
      action: handleSubmitForApproval,
      icon: PaperAirplaneIcon,
      textColor: "text-blue-700",
      bgColor: "bg-blue-50",
      hidden: false,
      menu: true,
    }] : []),
    ...(canApproveDocument ? [{
      component: null,
      label: t("Godkjenn revisjon"),
      action: () => {
        navigate(`/dashboard/hse-ks/documents/${id}/approve`);
      },
      icon: CheckCircleIcon,
      textColor: "text-green-700",
      bgColor: "bg-green-50",
      hidden: false,
      menu: true,
    }] : []),
    ...(isAdmin ? [{
      component: null,
      label: t("Administrer tilgang"),
      action: () => setShowAccessDialog(true),
      icon: UserGroupIcon,
      textColor: "text-gray-700",
      bgColor: "bg-white",
      hidden: false,
      menu: true,
    }] : []),
  ];

  return (
    <DashboardHeader
      title={document.title || `Dokument #${id}`}
      subtitle="Dokumentdetaljer med versjonshistorikk"
      actions={headerActions}
      mainButton={canEdit ? {
        label: document.status === 'approved' ? t("Ny revisjon") : t("Rediger"),
        action: document.status === 'approved' ? handleCreateRevision : () => {
          // Check if there's a draft version - if so, show it first
          const draftVersion = versions.find(v => v.status === 'draft');
          if (draftVersion) {
            setSelectedVersionId(draftVersion.id);
            setDisplayContent(draftVersion.content);
            setDisplayAttachedDocument(resolveVersionAttachment(draftVersion));
            // Scroll to version in history
            setTimeout(() => {
              const versionElement = window.document.querySelector(`[data-version-id="${draftVersion.id}"]`);
              if (versionElement) {
                versionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                versionElement.classList.add('bg-blue-50');
                setTimeout(() => {
                  versionElement.classList.remove('bg-blue-50');
                }, 2000);
              }
            }, 100);
          } else {
            navigate(`/dashboard/hse-ks/documents/${id}/edit`);
          }
        },
        path: null,
        color: "",
        icon: document.status === 'approved' ? DocumentArrowDownIcon : PencilIcon,
        // Show edit button for all statuses except when it's a new document (which shouldn't happen here)
      } : null}
      backButton={{
        label: t("Tilbake"),
        action: () => navigate("/dashboard/hse-ks/documents"),
        icon: ArrowLeftIcon,
      }}
    >
      <div className="mt-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Document Info - Compact */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
          <div className="px-4 py-4 sm:px-6">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-3 mb-2">
                  <DocumentTextIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <h3 className="text-lg leading-6 font-medium text-gray-900 truncate">
                    {document.title}
                  </h3>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 ml-8">
                  <span className="font-medium">{document.document_type_display || document.document_type}</span>
                  <span>{t("Versjon")} {document.current_version}</span>
                  {document.document_number && <span className="font-mono">{document.document_number}</span>}
                  {document.category && (
                    <span>{document.category_display || document.category.name}</span>
                  )}
                  {document.chapter && (
                    <span>{document.chapter.number} - {document.chapter.title}</span>
                  )}
                  {document.discipline && (
                    <span>{document.discipline.name}</span>
                  )}
                </div>
                {document.description && (
                  <p className="mt-2 text-sm text-gray-600 ml-8 line-clamp-2">
                    {document.description}
                  </p>
                )}
              </div>
              <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
                {document.status === "approved" ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircleIcon className="h-3 w-3 mr-1" />
                    {document.status_display || t("Godkjent")}
                  </span>
                ) : document.status === "pending_approval" ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    <ClockIcon className="h-3 w-3 mr-1" />
                    {document.status_display || t("Venter på godkjenning")}
                  </span>
                ) : document.status ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {document.status_display || document.status}
                  </span>
                ) : null}
              </div>
            </div>
            {(document.created_by || document.approved_by) && (
              <div className="mt-3 pt-3 border-t border-gray-200 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 ml-8">
                {document.created_by && (
                  <span>{t("Opprettet av")} {document.created_by.name} {document.created_at && `• ${new Date(document.created_at).toLocaleDateString()}`}</span>
                )}
                {document.approved_by && (
                  <span>{t("Godkjent av")} {document.approved_by.name} {document.updated_at && `• ${new Date(document.updated_at).toLocaleDateString()}`}</span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Document Content */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              {t("Innhold")}
            </h3>
            {selectedVersionId && versions.length > 0 && (
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  {t("Viser")}: {versions.find(v => v.id === selectedVersionId)?.status === 'approved' 
                    ? t("Godkjent versjon") 
                    : versions.find(v => v.id === selectedVersionId)?.status === 'draft'
                    ? t("Kladd versjon")
                    : versions.find(v => v.id === selectedVersionId)?.status === 'pending_approval'
                    ? t("Versjon venter på godkjenning")
                    : t("Versjon")} {versions.find(v => v.id === selectedVersionId)?.version}
                </p>
                {versions.find((v) => v.id === selectedVersionId)?.status === "approved" && currentUser?.id && (
                  <div className="mt-3 rounded-md border border-indigo-100 bg-indigo-50/80 p-3">
                    {myAcknowledged === true ? (
                      <p className="text-sm text-indigo-900 flex items-center gap-2">
                        <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
                        {t("hse.document.acknowledge_read_done")}
                      </p>
                    ) : (
                      <>
                        <p className="text-sm text-indigo-900 mb-2">{t("hse.document.acknowledge_read_hint")}</p>
                        <button
                          type="button"
                          onClick={handleAcknowledgeRead}
                          disabled={ackSubmitting || myAcknowledged === null}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {ackSubmitting ? t("hse.document.acknowledge_read_submitting") : t("hse.document.acknowledge_read")}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="prose max-w-none overflow-x-auto promang-rich-text-content">
              {(() => {
                const selectedVersion = versions.find(v => v.id === selectedVersionId);
                const contentType =
                  selectedVersion?.content_type ||
                  document.content_type ||
                  "text";
                const attachedDocument =
                  displayAttachedDocument ||
                  selectedVersion?.attached_document ||
                  document.attached_document ||
                  null;
                const attachedDocumentId =
                  attachedDocument?.id ||
                  selectedVersion?.attached_document_id ||
                  document.attached_document_id ||
                  null;
                const hasFileContent =
                  contentType === "file" || !!attachedDocumentId;

                if (hasFileContent) {
                  return (
                    <DocumentFileContent
                      attachedDocument={attachedDocument}
                      attachedDocumentId={attachedDocumentId}
                    />
                  );
                }

                if (displayContent || document.content) {
                  return (
                    <div dangerouslySetInnerHTML={{ __html: displayContent || document.content }} />
                  );
                }

                return <p className="text-gray-500">{t("Ingen innhold")}</p>;
              })()}
            </div>
          </div>
        </div>

        {/* Version History */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
          <div className="px-4 py-5 sm:px-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                <HistoryIcon className="h-5 w-5 mr-2" />
                {t("Versjonshistorikk")}
              </h3>
              <RoleOnly role="hse_ks_documents" action="update">
                <button
                  onClick={handleCreateRevision}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                  {t("Ny revisjon")}
                </button>
              </RoleOnly>
            </div>
            <div className="border-t border-gray-200">
              {versions.length === 0 ? (
                <p className="px-4 py-5 text-sm text-gray-500">{t("Ingen versjoner")}</p>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {versions.map((version) => {
                    const isSelected = selectedVersionId === version.id;
                    return (
                    <li 
                      key={version.id} 
                      data-version-id={version.id} 
                      className={`px-4 py-4 transition-colors duration-200 cursor-pointer hover:bg-gray-50 ${
                        isSelected ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                      }`}
                      onClick={() => {
                        setSelectedVersionId(version.id);
                        setDisplayContent(version.content);
                        setDisplayAttachedDocument(resolveVersionAttachment(version));
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            {version.is_approved ? (
                              <CheckCircleIcon className="h-5 w-5 text-green-500" />
                            ) : (
                              <ClockIcon className="h-5 w-5 text-yellow-500" />
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {t("Versjon")} {version.version}
                              {version.status === 'approved' && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                  {t("Godkjent")}
                                </span>
                              )}
                              {version.status === 'pending_approval' && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                  {t("Venter på godkjenning")}
                                </span>
                              )}
                              {version.status === 'draft' && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                  {t("Kladd")}
                                </span>
                              )}
                              {version.status === 'obsolete' && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                  {t("Foreldet")}
                                </span>
                              )}
                            </p>
                            <p className="text-sm text-gray-500">
                              {version.created_by?.name || t("Ukjent")} • {version.created_at && new Date(version.created_at).toLocaleString()}
                            </p>
                            {version.change_summary && (
                              <p className="text-sm text-gray-500 mt-1">{version.change_summary}</p>
                            )}
                            {version.approvals_count > 0 && (
                              <p className="text-xs text-gray-400 mt-1">
                                {version.approvals_count} {t("godkjenninger")}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {isSelected && (
                            <span className="text-xs text-blue-600 font-medium">{t("Vises")}</span>
                          )}
                          {canEdit && version.status === 'draft' && !version.is_approved && (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/dashboard/hse-ks/documents/${id}/edit`);
                                }}
                                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                              >
                                {t("Rediger")}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSubmitForApproval();
                                }}
                                className="text-sm text-green-600 hover:text-green-800 font-medium"
                              >
                                {t("Send til godkjenning")}
                              </button>
                            </>
                          )}
                          {canEdit && version.status === 'pending_approval' && !version.is_approved && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/dashboard/hse-ks/documents/${id}/edit`);
                              }}
                              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                            >
                              {t("Rediger")}
                            </button>
                          )}
                          {canApprove && !version.is_approved && version.status === 'pending_approval' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApproveVersion(version.id);
                              }}
                              disabled={approvingVersion === version.id}
                              className="text-sm text-green-600 hover:text-green-800 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                            >
                              {approvingVersion === version.id ? t("Godkjenner...") : t("Godkjenn")}
                            </button>
                          )}
                          {/* Print button for any version */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePrint(version.id);
                            }}
                            disabled={
                              (version.status !== 'approved' && !isAdmin) || 
                              printingVersionId === version.id
                            }
                            className="text-sm text-gray-600 hover:text-gray-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            title={
                              (version.status !== 'approved' && !isAdmin)
                                ? t("Kun godkjente versjoner kan skrives ut")
                                : printingVersionId === version.id
                                ? t("Genererer PDF...")
                                : t("Skriv ut versjon")
                            }
                          >
                            {printingVersionId === version.id ? (
                              <svg className="animate-spin h-4 w-4 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <PrinterIcon className="h-4 w-4 inline" />
                            )}
                          </button>
                        </div>
                      </div>
                    </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Access Dialog */}
      {isAdmin && (
        <DocumentAccessDialog
          documentId={id}
          open={showAccessDialog}
          onClose={() => setShowAccessDialog(false)}
        />
      )}
    </DashboardHeader>
  );
};

